/**
 * EarningBlock — what a surface asks people to do, and what it pays.
 *
 * ## The card carries a CATEGORY RING, and the widget never learns what a
 * ## category means
 *
 * Rules group into kinds — post something, buy something, complete a task —
 * and a surface that rings each card in the colour of its kind tells you what
 * a rule wants before you have read it. That grouping is the BRAND's
 * vocabulary, not ours: one brand's kinds are its own community, its socials
 * and its store; another's are entirely different words.
 *
 * So `category` is an opaque string. The component sets it as a data
 * attribute and composes a custom-property NAME from it —
 * `--earn-category-{key}` — which CSS then resolves. Adding a kind is a value
 * in a brand's token file plus a string in config. It is never a branch here,
 * never an enum to widen, and no brand's words appear in this file.
 *
 * A surface that supplies no categories gets the neutral ring, which is a
 * complete rendering of categories-not-present rather than a degraded one.
 *
 * ## Grid, not a list
 *
 * The rules are peers and are scanned, not read in order, so they take a grid
 * that answers to its neighbour: two across, three once there is room, and
 * back to two when a column widget is placed beside it. That last rule is the
 * occupancy signal the structure layer publishes — a widget that only knows
 * the viewport produces the gaps the design corpus spent six passes removing.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { BoardEarnObjective } from "@devour/client";
import {useGetBoardEarnObjectives} from "@/hooks/ledger-boards/useGetBoardEarnObjectives";
import {SerialActionForm, FollowActionForm, LikeActionForm, PostActionForm} from "./earn/EarnActionForms";
import {ReferralActionForm} from "./earn/ReferralActionForm";
import { FaCheck } from "react-icons/fa";
import "./EarningBlock.scss";

/**
 * The input form a card's back side opens, from the objective's own explicit
 * configuration — authored facts, never inference. Each config names exactly
 * one action: a referral config is the invite action, a serial vendor is the
 * serial action, a hashtag means a submitted post is verified, POST links are
 * the like action, an account link is the follow action. Like and Follow are
 * two different events with two different kinds of link — a post is never an
 * account — so nothing here has to tell same-shaped things apart. No config =
 * scored automatically; content only.
 */
function actionFormFor(rule: BoardEarnObjective): typeof SerialActionForm | null {
    const a = rule.action;
    if (!a) return null;
    if (a.referral) return ReferralActionForm;
    if (a.serial) return SerialActionForm;
    if (a.hashtags?.length) return PostActionForm;
    if (a.postUrls?.length) return LikeActionForm;
    if (a.accountUrl) return FollowActionForm;
    return null;
}

interface EarningBlockProps {
    /**
     * The section's heading — the shared block field every other section
     * uses. This block used to carry its own `title`/`subtitle`/`pointsLabel`
     * config class, which was a second copy of a field the block model
     * already has, and of the per-card unit the objective already supplies.
     */
    sectionTitle?: string;
    /**
     * The board this surface places. Its assigned objectives ARE the cards —
     * header, blurb, back-side body and the points that actually pay all come
     * from the objective and its reward, so assigning one is the whole
     * authoring step.
     */
    ledgerBoardId?: string;
}


/**
 * A category key arrives from operator-authored config, so it reaches CSS as
 * part of a property name and must not be able to carry anything else there.
 * Anything outside the safe set falls back to the neutral ring rather than
 * being sanitised into a different key — a silently rewritten category would
 * colour a card wrongly and look deliberate.
 */
const SAFE_CATEGORY = /^[a-z0-9-]{1,32}$/i;

/**
 * The category, or nothing — ONE gate, so every consumer of the value agrees.
 *
 * Previously only `ringFor` applied the guard while the data attribute took
 * the raw string. No injection followed from that (React sets attributes
 * through the DOM rather than splicing strings), but two paths disagreeing
 * about whether a value is acceptable is how the unguarded one later becomes
 * a selector target nobody re-checks.
 */
const safeCategory = (category?: string): string | undefined =>
    category && SAFE_CATEGORY.test(category) ? category.toLowerCase() : undefined;

const ringFor = (category?: string): CSSProperties | undefined => {
    const key = safeCategory(category);
    if (!key) return undefined;
    return {
        "--earn-ring": `var(--earn-category-${key}, var(--earn-category-default))`,
        /*
         * The value pill's INK, per category — the anchor's own move
         * (`.ptc--purchase .ptc__v { color:#1a0f00 }`, rttf-drop-hub:661):
         * white fails on a bright ring, so a brand may publish
         * `--earn-category-{key}-ink` beside the ring color (an ordinary
         * `earnCategories` entry, key "{key}-ink"). Unset falls back to
         * white, which is correct for every dark ring.
         */
        "--earn-value-ink": `var(--earn-category-${key}-ink, #fff)`,
    } as CSSProperties;
};

/**
 * One earn card.
 *
 * A CARD THAT NAMES AN ACTION MUST BE ABLE TO BACK IT. These are authored
 * copy — the block renders whatever rules the operator wrote — so a rule
 * pointing at an objective that is not assigned to a running experience
 * still drew a full card, invited the click, and opened a detail view that
 * could only apologise. The member is offered points for something the
 * platform will not accept.
 *
 * So a rule that declares an action resolves its policy FIRST, and the
 * card does not render at all unless that policy comes back. Rules with no
 * action are informational copy about pipeline-scored actions — they
 * have no policy to resolve and always render.
 *
 * Failure hides; LOADING does not. Hiding while the query is in flight would
 * pop cards into the grid a moment after paint.
 */
function EarnRuleCard({rule, unitLabel, onOpen}: {
    rule: BoardEarnObjective;
    unitLabel: string;
    onOpen: (el: HTMLButtonElement) => void;
}): JSX.Element | null {
    /* Used up for now (RTTR's claimed card): a check where the points were, and no tap. */
    const done = rule.claimComplete === true;
    return (
        <button
            type="button"
            className={done ? "earning__card earning__card--done" : "earning__card"}
            data-earn-category={safeCategory(rule.category)}
            style={ringFor(rule.category)}
            disabled={done}
            aria-label={done ? `${rule.name} — done` : undefined}
            onClick={e => onOpen(e.currentTarget)}
        >
            <div className="earning__card-inner">
                <div className="earning__card-head">
                    {done
                        ? <span className="earning__value earning__value--done"><FaCheck aria-hidden="true"/></span>
                        : <>
                            <span className="earning__value">
                                {rule.points.toLocaleString()}
                            </span>
                            <span className="earning__each">{unitLabel}</span>
                        </>
                    }
                </div>
                <span className="earning__label">{rule.name}</span>
                {rule.description &&
                    <span className="earning__desc">{rule.description}</span>
                }
            </div>
        </button>
    );
}

export const EarningBlock = ({ sectionTitle, ledgerBoardId }: EarningBlockProps) => {
    /*
     * The detail view — every card opens for more (Shelly 2026-08-28: "each
     * event card will need the ability to click for more info"). The open
     * card's index is state here because the modal is THIS block's detail,
     * not a page concern. Focus is managed the same way LedgerBoard's
     * breakdown modal manages it: close button takes focus on open, the
     * trigger card gets it back on close, Escape closes.
     */
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    /*
     * Mobile-only section fold, same affordance as every spine section:
     * `−` open / `+` collapsed. The control only renders past 700px via CSS.
     */
    const [folded, setFolded] = useState(false);
    const closeRef = useRef<HTMLButtonElement>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (openIndex == null) {
            return;
        }
        closeRef.current?.focus();
        /* The page behind a modal must not scroll under the member's finger. */
        const priorOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpenIndex(null);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = priorOverflow;
            window.removeEventListener("keydown", onKey);
            triggerRef.current?.focus();
        };
    }, [openIndex]);

    const {data: boardObjectives} = useGetBoardEarnObjectives(ledgerBoardId);

    /*
     * THE CARDS ARE THE BOARD'S ASSIGNED OBJECTIVES — the whole content of
     * the section. The block itself carries nothing but its heading.
     */
    const rules = boardObjectives?.objectives;

    if (!rules?.length) return null;

    const title = sectionTitle || "How to Earn Points";
    /*
     * The chip is boxed-value + suffix — "[40] EACH" — per the anchor's
     * `.ptc__b`; the suffix names the unit of repetition, not the currency.
     * The objective supplies it from its own `rewardPer` — a like is PER
     * LIKE, a detector registration PER DETECTOR — so "Each" is only the
     * fallback for an objective that repeats singly.
     */
    const labelFor = (rule: BoardEarnObjective): string => rule.pointsLabel || "Each";
    const openRule = openIndex != null ? rules[openIndex] : null;
    const ActionForm = openRule ? actionFormFor(openRule) : null;

    return (
        <div className={folded ? "earning earning--folded" : "earning"}>
            <div className="earning__head">
                <h3 className="earning__title">{title}</h3>
                <button
                    type="button"
                    className="earning__fold"
                    onClick={() => setFolded(f => !f)}
                    aria-expanded={!folded}
                    aria-label={folded ? "Expand section" : "Collapse section"}
                >
                    {folded ? "+" : "−"}
                </button>
            </div>

            <div className="earning__grid">
                {rules.map((rule, index) =>
                    <EarnRuleCard
                        key={rule.objectiveTemplateId}
                        rule={rule}
                        unitLabel={labelFor(rule)}
                        onOpen={el => {
                            triggerRef.current = el;
                            setOpenIndex(index);
                        }}
                    />)}
            </div>

            {openRule &&
                <div
                    className="earning__modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={openRule.name}
                    onClick={e => {
                        if (e.target === e.currentTarget) setOpenIndex(null);
                    }}
                >
                    <div
                        className="earning__modal-card"
                        data-earn-category={safeCategory(openRule.category)}
                        style={ringFor(openRule.category)}
                    >
                        {/*
                          * ONE inner panel, clipped to the same polygon as the
                          * ring — the pattern `__card-inner` already uses. The
                          * children used to sit directly on the ring, each
                          * painting its own background, so nothing followed the
                          * bevel and the notch read as a broken corner.
                          */}
                        <div className="earning__modal-inner">
                            <div className="earning__modal-head">
                                <span className="earning__value">
                                    {openRule.points.toLocaleString()}
                                </span>
                                <span className="earning__each">{labelFor(openRule)}</span>
                                <button
                                    ref={closeRef}
                                    type="button"
                                    className="earning__modal-close"
                                    onClick={() => setOpenIndex(null)}
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <h4 className="earning__modal-title">{openRule.name}</h4>
                            {openRule.description &&
                                <p className="earning__modal-desc">{openRule.description}</p>
                            }
                            {openRule.instructions &&
                                <p className="earning__modal-details">{openRule.instructions}</p>
                            }
                            {ActionForm && openRule.objectiveTemplateId &&
                                <div className="earning__modal-action">
                                    <ActionForm
                                        objectiveTemplateId={openRule.objectiveTemplateId}
                                        label={openRule.name}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

export default EarningBlock;
